import { RefObject, useCallback } from 'react';
import { getFocusableElements } from 'src/utils/html';

const INPUT_INDEX = 0;

interface ArrowNavigationOptions {
  /** Off: the arrows stop at the ends instead of cycling. */
  wrap?: boolean;
  /** Takes ArrowUp on the first element, and typing, back to a field outside the container. */
  onExitTop?: () => void;
}

const isTextField = (element: Element | null) =>
  element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement;

export const useArrowNavigation = ({ wrap = true, onExitTop }: ArrowNavigationOptions = {}) =>
  useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>, dialogRef: RefObject<HTMLElement | null>) => {
      const { key, code } = event;
      const focusableElements = getFocusableElements(dialogRef.current);
      if (!focusableElements) {
        return;
      }

      const lastIndex = focusableElements.length - 1;
      const currentIndex = focusableElements.findIndex(
        (element) => element === document.activeElement,
      );
      let nextIndex = currentIndex;

      if (key === 'ArrowDown' || (key === 'ArrowRight' && currentIndex !== INPUT_INDEX)) {
        nextIndex = wrap
          ? (currentIndex + 1) % focusableElements.length
          : Math.min(currentIndex + 1, lastIndex);
      } else if (key === 'ArrowUp' || (key === 'ArrowLeft' && currentIndex !== INPUT_INDEX)) {
        if (!wrap && currentIndex === 0 && onExitTop) {
          event.preventDefault();
          onExitTop();
          return;
        }
        const previousIndex = wrap ? lastIndex : 0;
        nextIndex = currentIndex > 0 ? currentIndex - 1 : previousIndex;
      } else if ((key === 'Home' || key === 'End') && !isTextField(document.activeElement)) {
        nextIndex = key === 'Home' ? 0 : lastIndex;
      } else if (code === `Key${key.toUpperCase()}` || code === `Digit${key}`) {
        // Moving focus on keydown lets the keystroke land in the field it moved to.
        if (onExitTop) {
          onExitTop();
          return;
        }
        focusableElements[INPUT_INDEX]?.focus();
        return;
      }

      if (currentIndex !== nextIndex) {
        event.preventDefault();
        focusableElements[nextIndex]?.focus();
      }
    },
    [wrap, onExitTop],
  );
